import styled, { css } from "styled-components";

const buttonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: 0 22px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 0.95rem;
  font-weight: 700;
  transition: transform 180ms ease, border-color 180ms ease, background 180ms ease,
    color 180ms ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

export const Page = styled.main`
  --bg: #101012;
  --bg-elevated: #17171b;
  --bg-card: #1f1f24;
  --bg-soft: rgba(255, 255, 255, 0.04);
  --border: rgba(255, 255, 255, 0.08);
  --text: #f4f4f5;
  --text-muted: rgba(244, 244, 245, 0.74);
  --text-soft: rgba(244, 244, 245, 0.54);
  --accent-start: #ff8a00;
  --accent-end: #f23d7f;

  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background:
    radial-gradient(circle at 12% 8%, rgba(242, 61, 127, 0.18), transparent 24%),
    radial-gradient(circle at 90% 0%, rgba(255, 138, 0, 0.18), transparent 24%),
    linear-gradient(180deg, #141417 0%, #0f1012 100%);
  color: var(--text);
`;

export const Glow = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(circle at 20% 20%, rgba(242, 61, 127, 0.18), transparent 20%),
    radial-gradient(circle at 78% 10%, rgba(255, 138, 0, 0.16), transparent 20%),
    radial-gradient(circle at 70% 84%, rgba(255, 255, 255, 0.04), transparent 28%);
`;

export const Shell = styled.div`
  position: relative;
  z-index: 1;
  width: min(1120px, calc(100% - 40px));
  margin: 0 auto;
  padding: 24px 0 64px;

  @media (max-width: 768px) {
    width: min(100% - 32px, 1120px);
    padding-bottom: 40px;
  }
`;

export const TopBar = styled.header`
  position: sticky;
  top: 16px;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 18px 22px;
  border: 1px solid var(--border);
  border-radius: 24px;
  background: rgba(18, 18, 21, 0.72);
  backdrop-filter: blur(18px);

  @media (max-width: 820px) {
    flex-wrap: wrap;
  }
`;

export const BrandLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  color: var(--text);
`;

export const BrandMark = styled.img`
  width: 132px;
  height: auto;
  object-fit: contain;

  @media (max-width: 640px) {
    width: 108px;
  }
`;

export const BrandCopy = styled.span`
  max-width: 260px;
  color: var(--text-soft);
  font-size: 0.92rem;
  line-height: 1.45;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const NavLinks = styled.nav`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 18px;

  @media (max-width: 820px) {
    width: 100%;
    order: 3;
  }
`;

export const NavAnchor = styled.a`
  color: var(--text-muted);
  font-size: 0.94rem;
  font-weight: 500;

  &:hover {
    color: var(--text);
  }
`;

export const HeaderAction = styled.a`
  ${buttonStyles};
  background: linear-gradient(135deg, var(--accent-start), var(--accent-end));
  color: #fff;
  box-shadow: 0 16px 32px rgba(242, 61, 127, 0.18);

  @media (max-width: 820px) {
    margin-left: auto;
  }
`;

export const Hero = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(300px, 0.95fr);
  gap: 34px;
  align-items: center;
  padding: 64px 0 40px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding-top: 42px;
  }
`;

export const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const Eyebrow = styled.span`
  display: inline-flex;
  width: fit-content;
  min-height: 34px;
  align-items: center;
  padding: 0 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #f8d29a;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const Title = styled.h1`
  font-family: "Space Grotesk", sans-serif;
  font-size: clamp(2.5rem, 5vw, 4.4rem);
  line-height: 0.98;
  letter-spacing: -0.05em;
  max-width: 720px;
`;

export const Description = styled.p`
  max-width: 680px;
  color: var(--text-muted);
  font-size: 1.05rem;
  line-height: 1.72;
`;

export const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const StatusCard = styled.div`
  padding: 18px;
  border-radius: 22px;
  background: rgba(18, 18, 21, 0.74);
  border: 1px solid var(--border);
  backdrop-filter: blur(16px);
`;

export const StatusLabel = styled.span`
  display: block;
  color: var(--text-soft);
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

export const StatusValue = styled.strong`
  display: block;
  margin-top: 10px;
  color: var(--text);
  font-size: 1.15rem;
  line-height: 1.45;
`;

export const PreviewCard = styled.div`
  position: relative;
  border-radius: 32px;
  overflow: hidden;
  min-height: 420px;
  border: 1px solid var(--border);
  background: rgba(19, 19, 22, 0.88);
  box-shadow: 0 26px 60px rgba(0, 0, 0, 0.26);
`;

export const PreviewImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.46;
`;

export const PreviewOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(15, 16, 18, 0.12) 0%, rgba(15, 16, 18, 0.88) 72%),
    linear-gradient(135deg, rgba(242, 61, 127, 0.16), rgba(255, 138, 0, 0.1));
`;

export const PreviewContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 420px;
  padding: 26px;
`;

export const PreviewBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  min-height: 32px;
  align-items: center;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: #f4f4f5;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export const PreviewTitle = styled.h2`
  margin-top: 16px;
  font-size: 1.9rem;
  line-height: 1.06;
`;

export const PreviewText = styled.p`
  margin-top: 12px;
  color: rgba(244, 244, 245, 0.82);
  line-height: 1.68;
  max-width: 480px;
`;

export const Section = styled.section`
  margin-top: 34px;
`;

export const SectionCard = styled.div`
  padding: 28px;
  border-radius: 28px;
  background: rgba(17, 18, 22, 0.8);
  border: 1px solid var(--border);

  @media (max-width: 640px) {
    padding: 22px;
  }
`;

export const SectionTitle = styled.h2`
  font-size: clamp(1.55rem, 3vw, 2.2rem);
  line-height: 1.08;
`;

export const SectionText = styled.p`
  margin-top: 12px;
  max-width: 760px;
  color: var(--text-muted);
  line-height: 1.74;
`;

export const ReasonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const ReasonCard = styled.div`
  padding: 22px;
  border-radius: 22px;
  background: var(--bg-soft);
  border: 1px solid var(--border);
`;

export const ReasonTitle = styled.h3`
  font-size: 1.05rem;
  line-height: 1.35;
`;

export const ReasonText = styled.p`
  margin-top: 10px;
  color: var(--text-muted);
  line-height: 1.7;
`;

export const FormShell = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.85fr) minmax(280px, 0.75fr);
  gap: 18px;
  margin-top: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const FormCard = styled.div`
  padding: 24px;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(31, 31, 36, 0.96), rgba(23, 23, 27, 0.92));
  border: 1px solid var(--border);
`;

export const FormTitle = styled.h3`
  font-size: 1.25rem;
`;

export const FormText = styled.p`
  margin-top: 10px;
  color: var(--text-muted);
  line-height: 1.7;
`;

export const WaitlistForm = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;
`;

export const EmailInput = styled.input`
  flex: 1 1 280px;
  min-height: 54px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: var(--text);
  padding: 0 16px;
  font-size: 0.98rem;

  &::placeholder {
    color: rgba(244, 244, 245, 0.38);
  }

  &:focus {
    outline: none;
    border-color: rgba(255, 138, 0, 0.68);
    box-shadow: 0 0 0 3px rgba(255, 138, 0, 0.14);
  }
`;

export const SubmitButton = styled.button`
  ${buttonStyles};
  cursor: pointer;
  background: linear-gradient(135deg, var(--accent-start), var(--accent-end));
  color: #fff;
  box-shadow: 0 18px 38px rgba(242, 61, 127, 0.18);
`;

export const Feedback = styled.p`
  margin-top: 14px;
  color: #ffd8a8;
  font-weight: 700;
`;

export const SideCard = styled.div`
  padding: 24px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--border);
`;

export const SideLabel = styled.span`
  display: block;
  color: #f8d29a;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const SideTitle = styled.h3`
  margin-top: 12px;
  font-size: 1.2rem;
`;

export const SideText = styled.p`
  margin-top: 10px;
  color: var(--text-muted);
  line-height: 1.7;
`;

export const Footer = styled.footer`
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 24px 0 0;
  color: var(--text-soft);
  font-size: 0.92rem;

  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

export const FooterLink = styled.a`
  color: var(--text-muted);

  &:hover {
    color: var(--text);
  }
`;
